import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "./cn.js";
import { decideApproval } from "./approvals-api.js";
import {
	formatClonePlan,
	patchFromSiteStatus,
	siteCloneNeedsPoll,
} from "./clone-status.js";
import {
	siteCloneInFlight,
	type ChatSiteClone,
	type PublishDecisionEvent,
	type SiteCloneCardEvent,
	type SitePublishPhase,
} from "./messages.js";
import {
	CheckIcon,
	ExternalLinkIcon,
	RocketIcon,
	XIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SiteFavicon, hostFromUrl } from "./SiteFavicon.js";

type Props = {
	clones: ChatSiteClone[];
	onOpenSitePreview?: (url: string) => void;
	/** Ask the agent to publish this site (opens a gated approval). */
	onPublish?: (siteId: string, siteName: string) => void;
	onPublishDecision?: (event: PublishDecisionEvent) => void;
	onCardEvent?: (event: SiteCloneCardEvent) => void;
};

const PHASE_COPY: Record<string, string> = {
	starting: "Starting your site…",
	queued: "Queued…",
	scraping: "Reading your pages…",
	images: "Saving photos & assets…",
	finalizing: "Putting the pages together…",
	rebuilding: "Rebuilding interactive bits…",
	building: "Building your preview…",
	uploading: "Uploading your preview…",
	ready: "Preview ready",
	failed: "Something went wrong",
	pending: "Queued…",
	cloning: "Working on your site…",
	progress: "Working on your site…",
};

const PUBLISH_COPY: Record<SitePublishPhase, string> = {
	pending_approval: "Waiting for your approval to publish…",
	publishing: "Going live…",
	deployed: "Published & live",
	failed: "Publish didn’t finish",
};

function publishedHref(clone: ChatSiteClone): string | null {
	if (clone.publishedUrl) return clone.publishedUrl;
	if (clone.hostname) return `https://${clone.hostname.replace(/^https?:\/\//i, "")}`;
	return null;
}

function PublishGate({
	clone,
	onPublishDecision,
}: {
	clone: ChatSiteClone;
	onPublishDecision?: (event: PublishDecisionEvent) => void;
}) {
	const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
	const [error, setError] = useState<string | null>(null);
	const approvalId = clone.publishApprovalId?.trim() ?? "";

	if (!approvalId) {
		return (
			<p className="mc-clone-card-publish-warn">
				Approval is open, but this card is missing the button id. Ask
				Mairko to publish again.
			</p>
		);
	}

	const decide = async (verdict: "approve" | "reject") => {
		if (busy) return;
		setBusy(verdict);
		setError(null);
		const result = await decideApproval({ approvalId, verdict });
		setBusy(null);
		if (!result.ok) {
			setError(result.error);
			return;
		}
		onPublishDecision?.({
			siteId: clone.siteId,
			siteName: clone.name,
			verdict,
			patch: {
				publishPhase: verdict === "approve" ? "publishing" : null,
				publishDecision: verdict === "approve" ? "approved" : "rejected",
			},
		});
	};

	return (
		<div className="mc-clone-card-gate">
			{error ? <p className="mc-clone-card-error">{error}</p> : null}
			<div className="mc-clone-card-gate-actions">
				<Button
					type="button"
					size="sm"
					className="mc-clone-card-cta"
					disabled={busy !== null}
					onClick={() => void decide("approve")}
				>
					{busy === "approve" ? (
						<Spinner className="size-3.5" />
					) : (
						<CheckIcon size={14} strokeWidth={1.75} />
					)}
					Approve
				</Button>
				<Button
					type="button"
					size="sm"
					variant="outline"
					disabled={busy !== null}
					onClick={() => void decide("reject")}
				>
					{busy === "reject" ? (
						<Spinner className="size-3.5" />
					) : (
						<XIcon size={14} strokeWidth={1.75} />
					)}
					Reject
				</Button>
			</div>
		</div>
	);
}

export function clonePhaseLabel(clone: ChatSiteClone): string {
	if (clone.status === "published" || clone.publishPhase === "deployed") {
		return "Published";
	}
	if (clone.phase === "ready" || clone.status === "preview_ready") {
		return PHASE_COPY.ready ?? "Preview ready";
	}
	if (clone.phase === "failed" || clone.status === "failed") {
		return PHASE_COPY.failed ?? "Something went wrong";
	}
	return (
		PHASE_COPY[clone.phase] ??
		PHASE_COPY[clone.status] ??
		"Working on your site…"
	);
}

export { patchFromSiteStatus, siteCloneNeedsPoll } from "./clone-status.js";

/** Keep clone/publish rows fresh even when the chat card is hidden. */
export function useSiteClonePoll(
	clones: readonly ChatSiteClone[],
	onPatch?: (siteId: string, patch: Partial<ChatSiteClone>) => void,
): void {
	const watch = clones
		.filter(siteCloneNeedsPoll)
		.map((clone) => clone.siteId)
		.sort()
		.join(",");
	const clonesRef = useRef(clones);
	clonesRef.current = clones;
	const onPatchRef = useRef(onPatch);
	onPatchRef.current = onPatch;

	useEffect(() => {
		if (!watch) return;
		const ids = watch.split(",");
		let cancelled = false;
		const tick = async () => {
			await Promise.all(
				ids.map(async (siteId) => {
					try {
						const response = await fetch(`/api/sites/${siteId}/status`, {
							credentials: "include",
						});
						if (!response.ok || cancelled) return;
						const json: unknown = await response.json();
						if (!json || typeof json !== "object") return;
						const rec = json as {
							ok?: unknown;
							site?: Record<string, unknown>;
						};
						if (rec.ok !== true || !rec.site || cancelled) return;
						const prev =
							clonesRef.current.find((row) => row.siteId === siteId) ??
							({
								siteId,
								name: "",
								sourceUrl: "",
								phase: "progress",
								status: "cloning",
								progress: 0,
							} satisfies ChatSiteClone);
						onPatchRef.current?.(siteId, patchFromSiteStatus(rec.site, prev));
					} catch {
						/* ignore transient */
					}
				}),
			);
		};
		void tick();
		const timer = window.setInterval(() => void tick(), 2500);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [watch]);
}

/**
 * Business-owner clone progress — calm agency card, not AI chrome.
 */
export function CloneSiteCards({
	clones,
	onOpenSitePreview,
	onPublish,
	onPublishDecision,
	onCardEvent,
}: Props) {
	const settled = clones.filter((clone) => !siteCloneInFlight(clone));
	if (settled.length === 0) return null;

	return (
		<div className="mc-clone-list" role="list" aria-label="Hosted sites">
			{settled.map((clone) => (
				<CloneSiteCard
					key={clone.siteId}
					clone={clone}
					onOpenSitePreview={onOpenSitePreview}
					onPublish={onPublish}
					onPublishDecision={onPublishDecision}
					onCardEvent={onCardEvent}
				/>
			))}
		</div>
	);
}

function CloneSiteCard({
	clone,
	onOpenSitePreview,
	onPublish,
	onPublishDecision,
	onCardEvent,
}: {
	clone: ChatSiteClone;
	onOpenSitePreview?: (url: string) => void;
	onPublish?: (siteId: string, siteName: string) => void;
	onPublishDecision?: (event: PublishDecisionEvent) => void;
	onCardEvent?: (event: SiteCloneCardEvent) => void;
}) {
	const ready =
		Boolean(clone.previewUrl) &&
		(clone.status === "preview_ready" || clone.phase === "ready");
	const failed = clone.status === "failed" || clone.phase === "failed";
	const published =
		clone.status === "published" ||
		clone.publishPhase === "deployed" ||
		Boolean(clone.publishedUrl?.trim());
	const awaitingApproval =
		clone.publishPhase === "pending_approval" &&
		clone.publishDecision !== "approved" &&
		clone.publishDecision !== "rejected";
	const plan = formatClonePlan(clone);
	const publishing =
		clone.publishPhase === "publishing" ||
		(clone.publishDecision === "approved" &&
			!published &&
			clone.publishPhase !== "failed");

	const progress = Math.max(0, Math.min(100, clone.progress || 0));
	const liveHref = publishedHref(clone);
	const host = hostFromUrl(clone.sourceUrl);
	const liveHost = liveHref ? hostFromUrl(liveHref) : null;
	const titleHost = hostFromUrl(clone.name || clone.sourceUrl);
	const title = titleHost === host ? host : clone.name || host;
	const fireCard = (
		action: SiteCloneCardEvent["action"],
		href?: string,
	) => {
		onCardEvent?.({
			siteId: clone.siteId,
			siteName: title,
			action,
			href,
		});
	};

	const statusLine = published
		? (liveHost && liveHost !== host ? liveHost : "Live on the web")
		: awaitingApproval
			? "Needs your approval"
			: publishing
				? "Going live…"
					: ready
						? "Preview ready"
						: failed
							? "Couldn’t finish"
							: "In progress";

	return (
		<article
			className={cn(
				"mc-clone-card",
				published && "mc-clone-card--published",
				ready && !published && "mc-clone-card--ready",
				failed && "mc-clone-card--failed",
			)}
			role="listitem"
		>
			<header className="mc-clone-card-head">
				<SiteFavicon
					url={clone.sourceUrl}
					live={!ready && !failed && !published}
				/>
				<div className="mc-clone-card-meta">
					<p className="mc-clone-card-title">{title}</p>
					<p
						className={cn(
							"mc-clone-card-url",
							published && "mc-clone-card-url--live",
						)}
						title={published ? (liveHref ?? clone.sourceUrl) : clone.sourceUrl}
					>
						{published ? (
							<span className="mc-clone-card-dot" aria-hidden />
						) : null}
						{statusLine}
					</p>
				</div>
				{published ? (
					<span className="integration-badge integration-badge--ok">
						Published
					</span>
				) : awaitingApproval ? (
					<span className="integration-badge integration-badge--warn">
						Needs approval
					</span>
				) : null}
			</header>

			{published || ready ? null : (
				<>
					<p className="mc-clone-card-phase">{clonePhaseLabel(clone)}</p>
					{plan ? <p className="mc-clone-card-eta">{plan}</p> : null}
				</>
			)}

			{!ready && !failed && !published ? (
				<div
					className="mc-clone-card-bar"
					role="progressbar"
					aria-valuenow={progress}
					aria-valuemin={0}
					aria-valuemax={100}
				>
					<span style={{ width: `${progress}%` }} />
				</div>
			) : null}

			{failed && clone.lastError ? (
				<p className="mc-clone-card-error">{clone.lastError}</p>
			) : null}

			{published && liveHref ? (
				<div className="mc-clone-card-actions">
					<a
						className="mc-clone-card-cta"
						href={liveHref}
						target="_blank"
						rel="noopener noreferrer"
						onClick={() => fireCard("view_live", liveHref)}
					>
						View live site
						<ExternalLinkIcon size={14} strokeWidth={1.75} />
					</a>
					{clone.lastError ? (
						<p className="mc-clone-card-error">{clone.lastError}</p>
					) : null}
				</div>
			) : ready && clone.previewUrl ? (
				<div className="mc-clone-card-actions">
					{onOpenSitePreview ? (
						<Button
							type="button"
							size="sm"
							className="mc-clone-card-cta"
							onClick={() => {
								onOpenSitePreview(clone.previewUrl!);
								fireCard("open_preview", clone.previewUrl ?? undefined);
							}}
						>
							Open preview
						</Button>
					) : (
						<a
							className="mc-clone-card-cta"
							href={clone.previewUrl}
							target="_blank"
							rel="noopener noreferrer"
							onClick={() =>
								fireCard("open_tab", clone.previewUrl ?? undefined)
							}
						>
							Open preview
							<ExternalLinkIcon size={14} strokeWidth={1.75} />
						</a>
					)}
					{onPublish && !publishing ? (
						<Button
							type="button"
							size="sm"
							variant="outline"
							className="mc-clone-card-publish-cta"
							onClick={() => onPublish(clone.siteId, clone.name)}
						>
							<RocketIcon size={14} strokeWidth={1.75} />
							Publish
						</Button>
					) : null}
				</div>
			) : null}

			{(awaitingApproval ||
				clone.publishDecision === "rejected" ||
				clone.publishPhase === "failed" ||
				Boolean(publishing && clone.lastError)) ? (
				<div
					className={cn(
						"mc-clone-card-publish",
						published && "mc-clone-card-publish--live",
						(clone.publishPhase === "failed" ||
							clone.publishDecision === "rejected") &&
							"mc-clone-card-publish--failed",
					)}
				>
					<p className="mc-clone-card-publish-phase">
						{published
							? PUBLISH_COPY.deployed
							: clone.publishPhase === "failed"
								? PUBLISH_COPY.failed
								: clone.publishDecision === "rejected"
									? "Publish cancelled"
									: publishing
										? PUBLISH_COPY.publishing
										: awaitingApproval
											? PUBLISH_COPY.pending_approval
											: clone.publishPhase
												? PUBLISH_COPY[clone.publishPhase]
												: ""}
						{publishing && clone.deployStatus
							? ` (${clone.deployStatus})`
							: ""}
					</p>
					{clone.publishPhase === "failed" && clone.lastError ? (
						<p className="mc-clone-card-error">{clone.lastError}</p>
					) : null}
					{onPublish && clone.publishPhase === "failed" ? (
						<Button
							type="button"
							size="sm"
							className="mc-clone-card-cta"
							onClick={() => onPublish(clone.siteId, clone.name)}
						>
							<RocketIcon size={14} strokeWidth={1.75} />
							Publish again
						</Button>
					) : null}
					{clone.takesPayments && awaitingApproval ? (
						<p className="mc-clone-card-publish-warn">
							⚠ This site takes live payments — review before approving.
						</p>
					) : null}
					{awaitingApproval ? (
						<PublishGate
							clone={clone}
							onPublishDecision={onPublishDecision}
						/>
					) : null}
				</div>
			) : null}
		</article>
	);
}
